/**
 * Tolerant readers for what a till stored with a receipt: the fiscal block
 * (`receipts.fiscal`) and the printed receipt (`receipts.receiptDocument`, the
 * receipt IR the till printed from).
 *
 * Both columns are JSON written by the till, by an importer, or by an older
 * build, so nothing here assumes a shape: a field is used only when it has the
 * expected type, and an element of a kind this page does not know is kept as
 * `unknown` rather than dropped (the operator should see that the paper had
 * something here). Nothing is parsed with the contracts' zod schemas on
 * purpose — a stored receipt that fails a newer schema must still be shown.
 */

export type FiscalBlockView = {
  receiptNumber: string | null;
  fiscalNumber: string | null;
  /** The register's own fiscal number (the IR fiscal block's `registrarId`). */
  registrarId: string | null;
  registrarLabel: string | null;
  timestampFormatted: string | null;
  signature: string | null;
  qrPayload: string | null;
  testDocument: boolean;
};

export type PrintedElement =
  | { kind: 'header'; retailerName: string | null; lines: string[] }
  | {
      kind: 'item_line';
      name: string;
      quantityFormatted: string;
      unitPriceFormatted: string | null;
      lineTotalFormatted: string;
      sku: string | null;
      vatRateFormatted: string | null;
      discountFormatted: string | null;
    }
  | {
      kind: 'totals_block';
      subtotalFormatted: string | null;
      discountFormatted: string | null;
      vatBreakdown: { rateFormatted: string; baseFormatted: string; vatFormatted: string }[];
      totalFormatted: string | null;
    }
  | { kind: 'fiscal_block'; block: FiscalBlockView; marking: string | null }
  | { kind: 'footer'; retailerFooterText: string | null; lines: string[] }
  | { kind: 'legal_text'; text: string }
  | { kind: 'register_document'; text: string }
  | { kind: 'unknown'; type: string; text: string[] };

export type PrintedReceipt = {
  locale: string | null;
  generatedAt: string | null;
  elements: PrintedElement[];
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function str(record: Record<string, unknown>, key: string): string | null {
  const value = record[key];
  return typeof value === 'string' && value.trim() !== '' ? value : null;
}

function strings(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((v): v is string => typeof v === 'string' && v !== '')
    : [];
}

function fiscalFrom(record: Record<string, unknown>): FiscalBlockView {
  return {
    receiptNumber: str(record, 'receiptNumber'),
    fiscalNumber: str(record, 'fiscalNumber'),
    registrarId: str(record, 'registrarId'),
    registrarLabel: str(record, 'registrarLabel'),
    timestampFormatted: str(record, 'timestampFormatted'),
    signature: str(record, 'signature'),
    qrPayload: str(record, 'qrPayload'),
    testDocument: record.testDocument === true,
  };
}

function hasAnyValue(view: FiscalBlockView): boolean {
  return (
    view.receiptNumber !== null ||
    view.fiscalNumber !== null ||
    view.registrarId !== null ||
    view.timestampFormatted !== null ||
    view.signature !== null ||
    view.qrPayload !== null
  );
}

function elementFrom(raw: unknown): PrintedElement | null {
  if (!isRecord(raw)) return null;
  const type = typeof raw.type === 'string' ? raw.type : '';
  switch (type) {
    case 'header':
      return { kind: 'header', retailerName: str(raw, 'retailerName'), lines: strings(raw.lines) };
    case 'item_line':
      return {
        kind: 'item_line',
        name: str(raw, 'name') ?? '',
        quantityFormatted: str(raw, 'quantityFormatted') ?? '',
        unitPriceFormatted: str(raw, 'unitPriceFormatted'),
        lineTotalFormatted: str(raw, 'lineTotalFormatted') ?? '',
        sku: str(raw, 'sku'),
        vatRateFormatted: str(raw, 'vatRateFormatted'),
        discountFormatted: str(raw, 'discountFormatted'),
      };
    case 'totals_block':
      return {
        kind: 'totals_block',
        subtotalFormatted: str(raw, 'subtotalFormatted'),
        discountFormatted: str(raw, 'discountFormatted'),
        vatBreakdown: Array.isArray(raw.vatBreakdown)
          ? raw.vatBreakdown.filter(isRecord).map((entry) => ({
              rateFormatted: str(entry, 'rateFormatted') ?? '',
              baseFormatted: str(entry, 'baseFormatted') ?? '',
              vatFormatted: str(entry, 'vatFormatted') ?? '',
            }))
          : [],
        totalFormatted: str(raw, 'totalFormatted'),
      };
    case 'fiscal_block':
      return {
        kind: 'fiscal_block',
        block: fiscalFrom(raw),
        marking: str(raw, 'testDocumentMarking'),
      };
    case 'footer':
      return {
        kind: 'footer',
        retailerFooterText: str(raw, 'retailerFooterText'),
        lines: strings(raw.lines),
      };
    case 'legal_text': {
      const text = str(raw, 'text');
      return text === null ? null : { kind: 'legal_text', text };
    }
    case 'register_document': {
      const text = str(raw, 'text');
      return text === null ? null : { kind: 'register_document', text };
    }
    default:
      return {
        kind: 'unknown',
        type: type === '' ? '?' : type,
        text: Object.entries(raw)
          .filter(([key, value]) => key !== 'type' && typeof value === 'string' && value !== '')
          .map(([, value]) => value as string),
      };
  }
}

/** The stored printed receipt, or null when there is none or it has no elements. */
export function readPrintedReceipt(receiptDocument: unknown): PrintedReceipt | null {
  if (!isRecord(receiptDocument) || !Array.isArray(receiptDocument.elements)) return null;
  const elements = receiptDocument.elements
    .map(elementFrom)
    .filter((e): e is PrintedElement => e !== null);
  if (elements.length === 0) return null;
  return {
    locale: str(receiptDocument, 'locale'),
    generatedAt: str(receiptDocument, 'generatedAt'),
    elements,
  };
}

/**
 * The text the register printed itself (the PRRO fiscal library's own
 * layout), or null when the till sent only Back Office's receipt elements.
 *
 * A till that prints through the fiscal library stores both: our elements
 * (header, items, totals, fiscal block, footer) and a `register_document`
 * with the text the register actually put on paper. The register's text is
 * the receipt; showing our elements as well would print it twice.
 */
export function registerDocumentText(receipt: PrintedReceipt): string | null {
  const texts = receipt.elements
    .filter(
      (e): e is Extract<PrintedElement, { kind: 'register_document' }> =>
        e.kind === 'register_document',
    )
    .map((e) => e.text);
  return texts.length === 0 ? null : texts.join('\n');
}

/**
 * The fiscal block to show: the stored `fiscal` column, completed from the
 * printed receipt's fiscal block (only the IR carries the register's own
 * number and the test-document flag). Null when neither says anything.
 */
export function readFiscalBlock(fiscal: unknown, receiptDocument: unknown): FiscalBlockView | null {
  const stored = isRecord(fiscal) ? fiscalFrom(fiscal) : null;
  const printed = readPrintedReceipt(receiptDocument)?.elements.find(
    (e): e is Extract<PrintedElement, { kind: 'fiscal_block' }> => e.kind === 'fiscal_block',
  )?.block;
  if (stored === null && printed === undefined) return null;
  const base = stored ?? printed!;
  const merged: FiscalBlockView = {
    receiptNumber: base.receiptNumber ?? printed?.receiptNumber ?? null,
    fiscalNumber: base.fiscalNumber ?? printed?.fiscalNumber ?? null,
    registrarId: base.registrarId ?? printed?.registrarId ?? null,
    registrarLabel: base.registrarLabel ?? printed?.registrarLabel ?? null,
    timestampFormatted: base.timestampFormatted ?? printed?.timestampFormatted ?? null,
    signature: base.signature ?? printed?.signature ?? null,
    qrPayload: base.qrPayload ?? printed?.qrPayload ?? null,
    testDocument: base.testDocument || printed?.testDocument === true,
  };
  return hasAnyValue(merged) ? merged : null;
}
